<template>
    <pc-loader
        v-bind:loading="requesting"
        v-bind:message="requestWaitMsg">
        <div v-if="show">
            <div v-if="showESignatureStatusHeader">
                {{ eSignatureStatusHeader }}
                <hr class="my-2" />
            </div>
            <div
                v-for="(documentStatus, index) in projectClientDocumentStatuses"
                v-bind:key="index">
                <legacy-envelope-document-status-display v-bind:document-status="documentStatus" />
            </div>
            <div class="d-flex align-items-center">
                <pc-button
                    v-bind:disabled="readonly"
                    v-on:click="showSignatureOptions">
                    {{ manageSignaturesBtnLabel }}
                </pc-button>
                <pc-transition transition="fade">
                    <div
                        v-if="hasResent"
                        class="ms-2 text-bubble-success">
                        {{ notificationsResentMsg }}
                    </div>
                </pc-transition>
            </div>
        </div>
    </pc-loader>
</template>

<script>
    import Vuex from "vuex";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { ESIGNATURE_CANCEL_EXPLANATION_TYPE } from "ESignatures/eSignatureConstants";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "Forms/Components/Elements/i18n/signatureRequest.i18n";
    import cancelSignaturesDialog from "ESignatures/Legacy/cancelSignaturesDialog.vue";
    import cancelResendDialog from "ESignatures/Legacy/cancelResendEnvelopeDialog.vue";
    import legacyEnvelopeDocumentStatusDisplay from "./legacyEnvelopeDocumentStatusDisplay.vue";

    export default {
        components: { legacyEnvelopeDocumentStatusDisplay },
        props: {
            showESignatureStatusHeader: { type: Boolean, required: true, default: null }
        },
        data() {
            return {
                requesting: false,
                requestWaitMsg: null
            };
        },
        computed: {
            show() {
                return this.projectClientDocumentStatuses && this.projectClientDocumentStatuses.length > 0;
            },
            anyElementsComplete() {
                return this.projectClientDocumentStatuses.some(
                    (clientDocumentStatus) => clientDocumentStatus.IsComplete
                );
            },
            anyElementsIncomplete() {
                return this.projectClientDocumentStatuses.some(
                    (clientDocumentStatus) => !clientDocumentStatus.IsComplete
                );
            },
            allSignaturesComplete() {
                return this.projectClientDocumentStatuses.every(
                    (clientDocumentStatus) => clientDocumentStatus.IsComplete
                );
            },
            projectClientDocumentStatuses() {
                return this.classicESignatureDocumentStatuses;
            },
            showCancelIncomplete() {
                return this.anyElementsComplete && this.anyElementsIncomplete;
            },
            readonly() {
                return this.baseReadonly || this.isPrefill || this.isView;
            },
            ...Vuex.mapState(PROJECT_FORM_NAMESPACE, {
                hasResent: (state) => state.hasResentESignatures
            }),
            ...Vuex.mapGetters({
                classicESignatureDocumentStatuses: PROJECT_FORM_TYPES.getters.classicESignatureDocumentStatuses,
                isView: PROJECT_FORM_TYPES.getters.isView
            }),
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            async requestSignatures() {
                this.requesting = true;
                this.requestWaitMsg = this.requestingSignaturesWaitMessage;
                try {
                    await this.$store.dispatch(PROJECT_FORM_TYPES.actions.requestSignatures, null, { root: true });
                } finally {
                    this.requesting = false;
                }
            },
            async showSignatureOptions() {
                await this.$store.dispatch(PROJECT_FORM_CONTROLLER_TYPES.actions.saveFormData, null, { root: true });
                if (this.allSignaturesComplete) {
                    this.showCancelDialog();
                } else {
                    this.showResendDialog();
                }
            },
            showCancelDialog() {
                if (this.readonly) {
                    return;
                }

                // We only show the cancel dialog directly if all signatures are complete, so
                // only show cancel explanation options that make sense for an envelope that's already
                // complete (e.g. no "resending notification email")
                const cancelExplanationOptions = [
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.UpdatingContents,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other
                ];

                this.$app.showDialog({
                    title: this.cancelSignaturesHeader,
                    customComponent: cancelSignaturesDialog,
                    customComponentOptions: {
                        message: this.aboutToCancelMessage,
                        showCancelIncomplete: this.showCancelIncomplete,
                        separateButtonRow: true,
                        showDontCancel: true,
                        cancelExplanationOptions: cancelExplanationOptions
                    },
                    customComponentListeners: {
                        "cancel-incomplete": this.cancelIncompleteDocuments,
                        "cancel-all": this.cancelAllDocuments,
                        "dont-cancel": this.dontCancel
                    }
                });
            },
            cancelAllDocuments(cancelExplanationData) {
                this.cancelDocuments(PROJECT_FORM_TYPES.actions.cancelAllSignatures, cancelExplanationData);
            },
            cancelIncompleteDocuments(cancelExplanationData) {
                this.cancelDocuments(PROJECT_FORM_TYPES.actions.cancelIncompleteSignatures, cancelExplanationData);
            },
            async cancelDocuments(action, cancelExplanationData) {
                if (this.isCancelExplanationValid(cancelExplanationData)) {
                    this.$app.closeDialog();
                    this.requesting = true;
                    this.requestWaitMsg = this.cancellingSignaturesWaitMessage;
                    try {
                        await this.$store.dispatch(action, cancelExplanationData, { root: true });
                    } finally {
                        this.requesting = false;
                    }
                }
            },
            // Validate that the explanation data exists, has a type, and
            // if the type is 'Other' the explanation detail must be provided
            isCancelExplanationValid(cancelExplanationData) {
                return (
                    cancelExplanationData &&
                    cancelExplanationData.explanationType != null &&
                    (cancelExplanationData.explanationType !== ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other ||
                        Boolean(cancelExplanationData.explanationDetail))
                );
            },
            dontCancel() {
                this.$app.closeDialog();
            },
            async showResendDialog() {
                if (this.readonly) {
                    return;
                }
                this.$app.showDialog({
                    title: this.signatureOptionsHeader,
                    customComponent: cancelResendDialog,
                    customComponentOptions: {
                        showCancelIncomplete: this.showCancelIncomplete
                    },
                    customComponentListeners: {
                        "resend-notifications": this.doResendNotifications,
                        "cancel-incomplete": this.cancelIncompleteDocuments,
                        "cancel-all": this.cancelAllDocuments
                    },
                    large: true,
                    buttons: [
                        {
                            text: this.closeBtnLabel,
                            primary: true,
                            result: "cancel"
                        }
                    ]
                });
            },
            async doResendNotifications() {
                this.$app.closeDialog();
                try {
                    this.requesting = true;
                    this.requestWaitMsg = this.resendingSignaturesWaitMessage;
                    await this.$store.dispatch(PROJECT_FORM_TYPES.actions.resendSignatureNotifications, null, {
                        root: true
                    });
                } finally {
                    this.requesting = false;
                }
            }
        }
    };
</script>
